import { Avatar, Chip, SvgIcon, SvgIconProps } from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import LanguageIcon from '@mui/icons-material/Language';

import { ChannelProvider, HsmLineStatus } from '@/services/hsm-backend';

/**
 * Provider avatars, chips and the Gupshup brand mark shared by the read-only
 * channels card (Empresa tab) and the "Alta de línea" screen, so both
 * surfaces render channels identically.
 */

/**
 * Gupshup brand mark (the two-bubble logo with stars) drawn inline so we
 * don't depend on an external CDN. Rendered monochrome via `currentColor`
 * to keep the look consistent with the Meta / generic provider avatars.
 * Source: `gs-upload.gupshup.io/.../new-logo-gupshup.svg` (the icon
 * portion only, viewBox cropped to 160×148).
 */
export const GupshupIcon = (props: SvgIconProps) => (
  <SvgIcon viewBox="0 0 160 148" {...props}>
    <path
      d="M145.13 20.73V90.93C145.13 101.78 136.34 110.57 125.49 110.57H74.22L66.3 118.49C64.12 120.67 65.66 124.39 68.74 124.39H94.52L116.1 145.97C119.8 149.67 126.13 147.05 126.13 141.82V124.4H131.32C146.59 124.4 158.96 112.02 158.96 96.76V34.56C158.96 26.93 152.77 20.74 145.14 20.74L145.13 20.73Z"
      fill="currentColor"
      opacity="0.55"
    />
    <path
      d="M120.94 0H17.28C7.74 0 0 7.74 0 17.28V86.39C0 95.93 7.74 103.67 17.28 103.67H32.84V128.01C32.84 133.24 39.17 135.86 42.87 132.16L71.36 103.67H120.95C130.49 103.67 138.23 95.93 138.23 86.39V17.28C138.23 7.74 130.49 0 120.95 0H120.94Z"
      fill="currentColor"
    />
    <path
      d="M71.1 48.9L93 57L71.1 65.1L63 87L54.9 65.1L33 57L54.9 48.9L63 27L71.1 48.9Z"
      fill="#FFF"
    />
    <path
      d="M94.78 27.22L105 31L94.78 34.78L91 45L87.22 34.78L77 31L87.22 27.22L91 17L94.78 27.22Z"
      fill="#FFF"
    />
  </SvgIcon>
);

/**
 * Provider avatar. `muted` renders it in a disabled grey tone (keeping the
 * provider icon) — used for channels without a `whatsappNumber`, which cannot
 * be onboarded yet.
 */
export const ProviderAvatar = ({
  provider,
  muted = false,
}: {
  provider: ChannelProvider | null;
  muted?: boolean;
}) => {
  const mutedSx = { bgcolor: 'grey.200', color: 'text.disabled' };

  if (provider === 'gupshup') {
    // Magenta Gupshup palette — distinct from Cliengo's primary purple.
    return (
      <Avatar sx={muted ? mutedSx : { bgcolor: '#FCE7EE', color: '#C2185B' }}>
        <GupshupIcon fontSize="small" />
      </Avatar>
    );
  }
  if (provider === 'meta_cloud') {
    return (
      <Avatar
        sx={muted ? mutedSx : { bgcolor: 'info.light', color: 'info.dark' }}
      >
        <FacebookIcon fontSize="small" />
      </Avatar>
    );
  }
  return (
    <Avatar sx={{ bgcolor: 'grey.200', color: 'text.secondary' }}>
      <LanguageIcon fontSize="small" />
    </Avatar>
  );
};

/**
 * Per-channelType brand palette for {@link ChannelTypeChip}: a tinted
 * background + brand-colored text. Types not listed fall back to the neutral
 * outlined chip.
 */
const CHANNEL_TYPE_STYLES: Record<string, { bgcolor: string; color: string }> =
  {
    WHATSAPP_API: { bgcolor: '#E7F8EF', color: '#128C7E' },
    WHATSAPP_BUSINESS: { bgcolor: '#E7F8EF', color: '#128C7E' },
    INSTAGRAM: { bgcolor: '#FBE4EF', color: '#C13584' },
    FACEBOOK: { bgcolor: '#E7F0FE', color: '#1877F2' },
    WEBSITE: { bgcolor: '#E0F7FA', color: '#00838F' },
  };

/**
 * Raw Kori `channelType` shown verbatim (monospaced) instead of the derived
 * provider name (Gupshup / Meta). Known channel types get their brand color;
 * unknown ones keep the neutral black-and-white outlined chip.
 */
export const ChannelTypeChip = ({ channelType }: { channelType?: string }) => {
  const style = channelType ? CHANNEL_TYPE_STYLES[channelType] : undefined;

  if (!style) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={channelType ?? '—'}
        sx={{ fontFamily: 'ui-monospace, monospace' }}
      />
    );
  }

  return (
    <Chip
      size="small"
      label={channelType}
      sx={{ ...style, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}
    />
  );
};

/**
 * HSM line status chip. Only rendered for channels that have a `whatsappNumber`
 * (channels without one show their state inline next to the avatar instead).
 */
export const HsmStatusChip = ({ status }: { status: HsmLineStatus | null }) => {
  if (status === 'active') {
    return <Chip size="small" color="success" label="Línea HSM activa" />;
  }
  if (status === 'inactive') {
    return <Chip size="small" color="warning" label="Línea HSM inactiva" />;
  }
  return <Chip size="small" color="warning" label="Sin HSM activo" />;
};
